import { useLayoutEffect, useRef, useState } from "react";

/**
 * Keeps an optional item in a flex row for as long as the row can actually hold it,
 * instead of dropping it at a breakpoint.
 *
 * Built for the navbar's secondary icons (recycle bin, release notes, product tours),
 * which should outlast everything else in the bar and go last. A breakpoint cannot
 * express that here, because the bar changes shape more than once on the way down: the
 * logo drops to a mark at `sm`, and the tabs drop their labels at 390px, so what the
 * icons are competing with is different again either side of each. Measuring covers all
 * of it without hardcoding any of it, and keeps working when the bar's contents change.
 *
 * Usage: `rowRef` on the flex row, `itemRef` on the optional item, and `fixedRef` on
 * each element whose width the row genuinely has to find room for. Register something
 * that reports the width it NEEDS: a leaf (the logo), or a `shrink-0` group that is
 * sized by its contents. A `flex-1` container reports whatever width it was allocated
 * instead, so registering one measures the row against itself and never sees slack.
 *
 * Registering a group that contains the optional item is fine, and is what the navbar
 * does: while the item is visible its width is inside the group's, so `required` is the
 * row's true total; once hidden it contributes nothing and `lastVisibleWidth` below is
 * what stands in for it.
 *
 * Anything that yields to the item rather than competing with it is left unregistered -
 * the clinic name shrinks and then hides, so it is not part of the requirement. Without
 * that the name's width would push the icons out early, which is backwards: the name is
 * the thing meant to go first.
 *
 * Hysteresis matters: once hidden the item has no width to measure, so re-showing it on
 * any spare space would re-overflow and hide it again next frame. The width it last
 * occupied while visible has to fit, with its gap, before it comes back - and the gap is
 * not optional bookkeeping. Leaving it out does not make the threshold generous, it makes
 * the two thresholds overlap, which is a show/hide loop rather than a loose boundary.
 */
export const useFitsInRow = () => {
    const rowRef = useRef<HTMLDivElement>(null);
    const itemElement = useRef<HTMLElement | null>(null);
    const fixedElements = useRef(new Set<HTMLElement>());
    const lastVisibleWidth = useRef(0);
    const [visible, setVisible] = useState(true);

    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) return;

        const measure = () => {
            // Nothing laid out yet (jsdom, or a hidden ancestor): the row reads 0 wide, and
            // with its gutter taken off the space available comes out negative, so everything
            // looks too big for it. Deciding on that drops the icons before the bar has ever
            // been too narrow - they blink out and back on every mount. The same guard, for
            // the same reason, as the one in useFitLevel and useFittingText.
            if (row.clientWidth === 0) return;

            const rowStyle = getComputedStyle(row);
            // clientWidth is the padding box, and this row carries a px-2/sm:px-4 gutter, so
            // the space the items actually have is that much narrower than the box they sit in.
            const padding = (parseFloat(rowStyle.paddingLeft) || 0) + (parseFloat(rowStyle.paddingRight) || 0);

            // Elements come and go (the tab strip unmounts while search is expanded), and a
            // callback ref does not always fire on the way out, so drop detached nodes here.
            let required = 0;
            let counted = 0;
            for (const el of Array.from(fixedElements.current)) {
                if (!el.isConnected) {
                    fixedElements.current.delete(el);
                    continue;
                }
                required += el.getBoundingClientRect().width;
                // The gap that separates an item is its own parent's, not the row's. These
                // items are not all siblings of one another (the logo and the tab strip sit
                // inside the row's flex-1 group), so reading the row's gap for all of them
                // would be wrong wherever a nested group is spaced differently.
                const parent = el.parentElement;
                required += parent ? parseFloat(getComputedStyle(parent).columnGap) || 0 : 0;
                counted += 1;
                // Registration happens on render, which can be after this effect ran - and a
                // tracked element can change width without the row doing so (the tab strip
                // unmounts while search is expanded). Observing here covers both; observe()
                // is idempotent per element.
                observer.observe(el);
            }
            if (!counted) return;

            const itemWidth = itemElement.current?.getBoundingClientRect().width ?? 0;
            if (itemWidth > 0) lastVisibleWidth.current = itemWidth;

            // The item's own gap goes back with it. While it is visible its width AND the gap
            // that separates it are already inside whichever registered element contains it;
            // hidden it is `display:none`, so it is not a flex item and neither is counted.
            // Adding the width back without the gap made the show threshold 8px looser than
            // the hide one, and across those 8px both were true: showing the icons re-
            // overflowed the row, which resized the cluster they live in, which measured
            // again and hid them, which resized it again. Measured at 520-526px in the list
            // view, the icons changed state ~120 times a second and never settled.
            const itemParent = itemElement.current?.parentElement;
            const itemGap = itemParent ? parseFloat(getComputedStyle(itemParent).columnGap) || 0 : 0;

            const available = row.clientWidth - padding;
            setVisible((wasVisible) =>
                wasVisible ? required <= available : required + lastVisibleWidth.current + itemGap <= available,
            );
        };

        const observer = new ResizeObserver(measure);
        measure();
        observer.observe(row);
        return () => observer.disconnect();
    }, []);

    const itemRef = (node: HTMLElement | null) => {
        itemElement.current = node;
    };

    const fixedRef = (node: HTMLElement | null) => {
        if (node) fixedElements.current.add(node);
    };

    return { rowRef, itemRef, fixedRef, visible };
};
